import React, { useState } from "react";
import axios from "axios";
import { CheckCircle2, ShieldAlert, Copy, ExternalLink, RefreshCw, CreditCard, Clock3 } from "lucide-react";
import { Button } from "./ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PayPalModal = ({ isOpen, onClose, user, onUnlockSuccess }) => {
  const [orderId, setOrderId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [resultMsg, setResultMsg] = useState("");

  if (!isOpen) return null;

  // Email y precio vienen del servidor (.env: PAYPAL_RECIPIENT y APP_PRICE_EUR)
  const paypalEmail = user?.paypal_recipient || "";
  const amount = (user?.app_price_eur ?? 2.99).toFixed(2);
  const price = `${amount.replace(".", ",")}€`;
  const directPaypalUrl = `https://www.paypal.com/cgi-bin/webscr?cmd=_xclick&business=${encodeURIComponent(paypalEmail)}&item_name=${encodeURIComponent("CortoViral Licencia Completa de por Vida")}&amount=${amount}&currency_code=EUR&no_shipping=1`;

  if (user?.payment_pending && !success) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-md p-4">
        <div data-testid="paypal-unlock-modal" className="w-full max-w-md bg-[#12141D] border border-amber-500/40 rounded-2xl p-6 text-center space-y-4">
          <Clock3 className="w-10 h-10 text-amber-400 mx-auto" />
          <h2 className="text-xl font-black text-white">Pago en revisión</h2>
          <p className="text-sm text-slate-300">Ya recibimos tu comprobante. En cuanto confirmemos el pago en PayPal tu cuenta quedará desbloqueada de por vida.</p>
          <Button variant="secondary" onClick={onClose} className="w-full">Cerrar</Button>
        </div>
      </div>
    );
  }

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(paypalEmail);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleConfirmPayment = async (e) => {
    e.preventDefault();
    if (!orderId.trim()) return;
    setSubmitting(true);
    setError("");

    try {
      const token = localStorage.getItem("cortoviral_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(
        `${API}/payments/request-paypal-unlock`,
        { paypal_order_id: orderId.trim(), notes: `Pago activación ${amount} EUR` },
        { headers, withCredentials: true }
      );
      setResultMsg(res.data?.message || "");
      setSuccess(true);
      onUnlockSuccess();
      setTimeout(() => onClose(), res.data?.pending ? 6000 : 1500);
    } catch (err) {
      setError(err.response?.data?.detail || "Error verificando el pago. Intenta de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-md p-4">
      <div 
        data-testid="paypal-unlock-modal"
        className="w-full max-w-lg bg-[#12141D] border border-amber-500/40 rounded-2xl p-6 sm:p-8 shadow-2xl relative overflow-hidden"
      >
        <div className="text-center mb-5">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-amber-500/10 border border-amber-500/30 text-amber-400 mb-3">
            <CreditCard className="w-7 h-7" />
          </div>
          <h2 className="text-2xl font-black tracking-tight text-white">
            Desbloquear CortoViral de por Vida
          </h2>
          <p className="text-sm text-slate-300 mt-1">
            Pago único de <strong>{price}</strong>, para siempre: {user?.pro_daily_credits ?? 10} Shorts al día (en vez de {user?.free_daily_credits ?? 2}), Piloto Automático, publicación automática, sin marca de agua y sin anuncios.
          </p>
        </div>

        {/* Tarjeta de instrucciones y BOTÓN DE PAGO DIRECTO */}
        <div className="bg-[#1A1D2B] border border-white/10 rounded-xl p-5 mb-5 space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase tracking-wider text-slate-400 font-bold">Importe a Pagar</span>
            <span className="text-2xl font-mono font-black text-amber-400">{price}</span>
          </div>

          {/* BOTÓN DE PAGO DIRECTO PAYPAL DESTACADO.
              Es un <button> y no un <a> a propósito: el anuncio Vignette de Monetag salta al pulsar enlaces
              y no debe interponerse justo cuando el usuario va a pagar. */}
          <button
            type="button"
            data-testid="paypal-direct-pay-button"
            onClick={() => {
              // Sin "noopener" en las opciones porque entonces window.open devuelve siempre null
              const w = window.open(directPaypalUrl, "_blank");
              if (w) w.opener = null;
              else window.location.href = directPaypalUrl; // bloqueador de ventanas: abrir en la misma pestaña
            }}
            className="w-full py-3.5 px-4 bg-[#0070BA] hover:bg-[#005ea6] text-white font-extrabold rounded-xl shadow-lg shadow-blue-900/40 flex items-center justify-center gap-2 text-sm tracking-wide transition-all transform active:scale-95"
          >
            <span>Pagar {price} Directo con PayPal</span>
            <ExternalLink className="w-4 h-4" />
          </button>

          <div className="border-t border-white/10 pt-3 flex flex-col gap-1.5">
            <span className="text-xs text-slate-400">O envía el pago manual desde tu app de PayPal a:</span>
            <div className="flex items-center justify-between bg-black/40 border border-white/10 rounded-lg p-2.5">
              <span className="text-xs sm:text-sm font-mono font-semibold text-white select-all">{paypalEmail}</span>
              <button
                type="button"
                data-testid="copy-paypal-email-btn"
                onClick={handleCopyEmail}
                className="text-xs bg-slate-800 hover:bg-slate-700 text-slate-200 px-2.5 py-1 rounded border border-white/10 flex items-center gap-1 transition-all"
              >
                {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? "Copiado" : "Copiar"}
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-xs flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {success ? (
          <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl text-center text-emerald-400 font-semibold text-sm flex items-center justify-center gap-2">
            <CheckCircle2 className="w-5 h-5 shrink-0" /> {resultMsg || "¡Listo!"}
          </div>
        ) : (
          <form onSubmit={handleConfirmPayment} className="space-y-4">
            {/* Nota de advertencia en el modal de pago */}
            <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl text-[11px] text-amber-300 leading-relaxed">
              <strong>Nota Importante:</strong> La compra de la app por {price} desbloquea el plan Pro de por vida. Esto <em>no incluye</em> recargas infinitas de los créditos de los proveedores de IA (Claude, Gemini, etc.), los cuales deben ser recargados o gestionados de forma independiente por cada usuario según su consumo.
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1">
                ID de Transacción / Comprobante de PayPal (tras pagar)
              </label>
              <input
                data-testid="paypal-tx-input"
                type="text"
                required
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
                placeholder="ID de transacción del recibo de PayPal (ej. 9TX12345AB678901C)"
                className="w-full bg-[#1A1D2B] border border-white/10 rounded-lg px-3.5 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
            </div>

            <div className="flex gap-3">
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                className="flex-1"
              >
                Cerrar
              </Button>
              <Button
                data-testid="paypal-confirm-unlock-btn"
                type="submit"
                disabled={submitting}
                className="flex-1 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold"
              >
                {submitting ? <RefreshCw className="w-4 h-4 animate-spin" /> : "Enviar comprobante"}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
